import { html } from "htm/preact";
import { useEffect, useMemo, useState } from "preact/hooks";
import { adminOp, deleteOwnTournament, listLibraryCourses } from "../http.js";
import { ShareButton, ShareOrCopyButton, organiserUrl } from "../components/ShareButton.js";
import { FormatConfigForm } from "../components/FormatConfigForm.js";
import { Icon } from "../components/Icon.js";
import { getClient } from "../client.js";
import { forgetTournament, getAuth, setAuth } from "../storage.js";
import { getIdentity, setAppMode } from "../identity.js";
import { navigate } from "../router.js";
import { formats } from "../../../shared/formats/index.js";
import { conventionalAllowance } from "../../../shared/formats/matchPlay.js";
import { computeLiveRoundResults, describeRoundRules } from "../../../shared/leaderboard.js";
import { FULL_ROUND, HALF_ROUND, holesPlayedCount } from "../../../shared/handicap.js";
import {
  computeCoverageMatrix,
  evenHoleRanges,
  generateSinglesRotation,
  generateTeamRotation,
  holesInvalidatedByTeamChange,
  narrowToCard,
  segmentsWithOneMore,
  validateSegments,
} from "../../../shared/rotation.js";
import type {
  Course,
  FinalTiebreakRule,
  HoleSet,
  Player,
  Round,
  Segment,
  TeeSet,
} from "../../../shared/types.js";
import { seatOffer } from "../../../shared/seats.js";
import { TERMS } from "../copy.js";
import { numOr } from "../util.js";

function uid(): string {
  return crypto.randomUUID();
}

/** Tee sets are picked from a dropdown on a phone, where the name alone ("Blue",
 * "Red (Female)") doesn't say which is longer or how it affects handicaps — so the
 * label carries rating/slope and total yardage too. */
function teeLabel(tee: TeeSet): string {
  const yards = tee.holes.reduce((sum, h) => sum + (h.yards ?? 0), 0);
  const parts: string[] = [];
  if (tee.rating != null && tee.slope != null) parts.push(`${tee.rating}/${tee.slope}`);
  else if (tee.slope != null) parts.push(`slope ${tee.slope}`);
  if (yards > 0) parts.push(`${yards.toLocaleString()} yds`);
  return parts.length ? `${tee.name} — ${parts.join(", ")}` : tee.name;
}

function ordinal(n: number): string {
  const suffix = n % 100 >= 11 && n % 100 <= 13 ? "th" : ["th", "st", "nd", "rd"][n % 10] ?? "th";
  return `${n}${suffix}`;
}

function blankPlayer(): Player {
  return { id: uid(), name: "", handicapIndex: 0, token: null, claimedAt: null };
}

function blankRound(): Round {
  return {
    id: uid(),
    name: "",
    date: null,
    courseId: "",
    teeSet: "",
    holeSet: "all18",
    formatId: "strokePlayNet",
    formatConfig: {},
    pointTable: [10, 6, 4, 2],
    teamPoints: { win: 8, loss: 3 },
    multiplier: 1,
    handicapAllowance: 100,
    segments: [],
  };
}

export function Setup({ code }: { code: string }) {
  const auth = getAuth(code);
  const identity = getIdentity(code);
  const client = getClient(code);
  const snapshot = client?.snapshot.value ?? null;
  const hydrated = client?.hydrated.value ?? false;
  const connStatus = client?.status.value ?? "connecting";

  const [players, setPlayers] = useState<Player[]>([]);
  const [rounds, setRounds] = useState<Round[]>([]);
  const [tiebreaker, setTiebreaker] = useState<FinalTiebreakRule>("split");
  const [name, setName] = useState("");
  const [courses, setCourses] = useState<Course[]>([]);
  const [seeded, setSeeded] = useState(false);
  // Kind as well as text: every failure path on this screen wrote into the same string,
  // which was then always painted in the "saved" style — so "Failed to save" and
  // "Can't save" arrived in the same reassuring green as a save that worked.
  const [status, setStatusRaw] = useState<{ text: string; ok: boolean } | null>(null);
  const setStatus_ = (text: string) => setStatusRaw({ text, ok: true });
  const setError_ = (text: string) => setStatusRaw({ text, ok: false });
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    listLibraryCourses()
      .then(setCourses)
      .catch(() => {});
  }, []);

  // Seed local editable state from the server exactly once, so live pushes don't clobber
  // in-progress edits.
  //
  // It must be the *server's* state, not the cached snapshot the client paints with
  // first. Seeding from cache and latching meant the authoritative frame was thrown away,
  // and since saving sends the whole roster, the next Save wrote a stale roster back —
  // silently reverting a rename made anywhere else. Worse, a cache holding an empty
  // roster seeded a single blank player with a fresh id, and saving that wiped everyone.
  useEffect(() => {
    if (seeded || !hydrated || !snapshot) return;
    setPlayers(snapshot.tournament.players.length ? snapshot.tournament.players : [blankPlayer()]);
    setRounds(snapshot.tournament.rounds);
    setTiebreaker(snapshot.tournament.finalTiebreaker);
    setName(snapshot.tournament.name);
    setSeeded(true);
  }, [snapshot, seeded, hydrated]);

  if (!auth || !identity?.isOrganizer) {
    return html`<p>${`Only the ${TERMS.organizer} can open setup.`} <a href="#/">Home</a></p>`;
  }
  // The tab is hidden in player mode, but the route still resolves and the admin token
  // is still on the device — so a bookmark, a back button or a typed URL lands here.
  // Nothing is broken; this screen just isn't what they asked the app to be right now.
  if (!identity.canOrganize) {
    return html`
      <div class="card card-accent">
        <p>
          You're playing this ${TERMS.event} at the moment, so setup is put away. Your
          ${TERMS.organizer} controls are still yours — this only changes what the app
          offers you.
        </p>
        <button onClick=${() => setAppMode(code, "organizer")}>
          <${Icon} name="sliders" size=${18} /> ${`Switch back to ${TERMS.Organizer}`}
        </button>
      </div>
    `;
  }
  if (!hydrated || !snapshot) {
    // Saving is an HTTP call anyway, so setup was never usable offline; better to say so
    // than to hand over an editor primed with stale data that overwrites on Save.
    return html`<p>
      ${connStatus === "offline"
        ? "Setup needs a connection so it can load the current players and rounds. Reconnecting…"
        : "Loading the current setup…"}
    </p>`;
  }
  const adminToken = auth.token;
  const originalName = snapshot.tournament.name;
  // Only needed to tell the organiser how many holes are outstanding before they
  // close a round; the library courses are already loaded for the round editor.
  const roundResults = computeLiveRoundResults(
    snapshot.tournament,
    new Map(courses.map((c) => [c.id, c])),
    snapshot.scores,
    snapshot.perHoleData,
  );

  /** The seat as the server has it, not as this device remembers it — the two can
   * differ for a moment after somebody else claims that name. */
  const seatId = snapshot.tournament.organizerPlayerId ?? null;
  const scoresFor = (playerId: string) =>
    snapshot.scores.filter((s) => s.playerId === playerId && s.strokes != null).length;
  /** Yours, free, or held by a phone — see `seatOffer`. Read from the server's roster
   * rather than the unsaved rows, because whether a name is held is the server's
   * answer, and a row typed a moment ago isn't there yet (it is free by definition). */
  const offerFor = (playerId: string): ReturnType<typeof seatOffer> => {
    const known = snapshot.tournament.players.find((p) => p.id === playerId);
    return known ? seatOffer(known, seatId) : playerId === seatId ? "yours" : "free";
  };

  function updatePlayer(idx: number, patch: Partial<Player>) {
    setPlayers((prev) => prev.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  }
  function removePlayer(idx: number) {
    const doomed = players[idx];
    // Deleting the row you're playing under is allowed — you might not be playing after
    // all — but it takes your seat with it, and that shouldn't be found out later.
    if (doomed && doomed.id === seatId) {
      const ok = confirm(
        `${doomed.name || "That player"} is the name you're playing under. Removing them ` +
          `takes you off the roster too — you'll still be the ${TERMS.organizer}.\n\nRemove?`,
      );
      if (!ok) return;
    }
    setPlayers((prev) => prev.filter((_, i) => i !== idx));
  }
  function updateRound(idx: number, patch: Partial<Round>) {
    setRounds((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }
  function removeRound(idx: number) {
    setRounds((prev) => prev.filter((_, i) => i !== idx));
  }

  /**
   * Changing who plays with whom in a single-ball round throws away the scores for those
   * holes — one ball per side is stored against a player, so a reshuffle carries a score
   * to the wrong side and leaves the other looking unplayed. The server does the clearing
   * so it can't be skipped; this is the part that makes sure it isn't a surprise.
   *
   * Returns false if the organizer backs out.
   */
  function confirmTeamChanges(): boolean {
    const affected = rounds
      .map((next) => {
        const before = snapshot!.tournament.rounds.find((r) => r.id === next.id) ?? null;
        const teeSet = courses
          .find((c) => c.id === next.courseId)
          ?.teeSets.find((t) => t.name === next.teeSet);
        const holes = holesInvalidatedByTeamChange(before, next, teeSet?.holes.length);
        const scored = holes.filter((h) =>
          snapshot!.scores.some((s) => s.roundId === next.id && s.hole === h && s.strokes != null),
        );
        return { name: next.name || "Untitled round", scored };
      })
      .filter((r) => r.scored.length > 0);

    if (affected.length === 0) return true;

    const detail = affected
      .map(
        (r) =>
          `• ${r.name}: ${r.scored.length} hole${r.scored.length === 1 ? "" : "s"} (${r.scored.join(", ")})`,
      )
      .join("\n");

    return confirm(
      `You've changed the teams on holes that already have scores.\n\n${detail}\n\n` +
        `Those scores will be cleared, because a scramble or alternate-shot side shares one ` +
        `ball — with different partners the old score no longer belongs to anyone. Scores on ` +
        `holes whose teams you didn't touch are kept.\n\nClear them and save?`,
    );
  }

  async function saveAll(): Promise<boolean> {
    if (!confirmTeamChanges()) {
      setError_("Nothing saved.");
      return false;
    }
    // The round editor shows these as inline problems, but only ever as a dismissible
    // note — nothing stopped Save itself, and the server doesn't check them either, so
    // a round with e.g. a player on two sides could be saved and pushed live to every
    // scorecard. Block only on `error`-level problems; `warning`s (an empty side, an
    // unscored hole) still score, just maybe not as intended, so those stay non-blocking.
    const activePlayerIds = players.filter((p) => p.name.trim()).map((p) => p.id);

    // A round can end up asking for a nine that no longer exists: it was built when its
    // course had eighteen holes, and an admin has since re-entered that course as a
    // nine. Scoring copes (holesInPlay falls back to the whole card) and the editor
    // shows what it will actually play, so this is the last step — writing that back, so
    // the stored round stops disagreeing with every screen that reads it.
    const cardOf = (round: Round) =>
      (courses
        .find((c) => c.id === round.courseId)
        ?.teeSets.find((t) => t.name === round.teeSet) ??
        courses.find((c) => c.id === round.courseId)?.teeSets[0])?.holes;
    const toSave = rounds.map((round) => {
      const card = cardOf(round);
      return card && card.length <= HALF_ROUND && round.holeSet !== "all18"
        ? { ...round, holeSet: "all18" as const }
        : round;
    });

    for (const round of toSave) {
      const holeCount = holesPlayedCount(cardOf(round), round.holeSet);
      const errors = validateSegments(activePlayerIds, round.segments, holeCount, {
        requiresSides: formats[round.formatId]?.scope === "team",
      }).filter((p) => p.level === "error");
      if (errors.length > 0) {
        setError_(`Can't save — "${round.name || "Untitled round"}": ${errors[0]!.message}`);
        return false;
      }
    }
    setStatus_("Saving…");
    try {
      await adminOp(code, adminToken, {
        type: "adminSetPlayers",
        players: players
          .filter((p) => p.name.trim())
          .map((p) => ({ id: p.id, name: p.name.trim(), handicapIndex: numOr(String(p.handicapIndex), 0) })),
      });
      await adminOp(code, adminToken, { type: "adminSetRounds", rounds: toSave });
      await adminOp(code, adminToken, { type: "adminSetFinalTiebreaker", rule: tiebreaker });
      if (name.trim() && name.trim() !== originalName) {
        await adminOp(code, adminToken, { type: "adminSetName", name: name.trim() });
      }
      setStatus_("Saved.");
      return true;
    } catch (err) {
      setError_(`Failed to save: ${(err as Error).message}`);
      return false;
    }
  }

  /** One Save. A draft becomes active on the first save — there was never a reason to
   * make starting a separate button. A game that has been ended stays ended; only the
   * explicit control below reopens it. */
  async function save() {
    // Backing out of the team-change warning must not start the game either.
    if (!(await saveAll())) return;
    if (snapshot!.tournament.status === "draft") {
      // saveAll() already reported "Saved." — if this fails, that message must not be
      // left standing while the tournament is silently still in draft.
      try {
        await adminOp(code, adminToken, { type: "adminSetStatus", status: "active" });
      } catch (err) {
        setError_(`Saved, but couldn't start the ${TERMS.event}: ${(err as Error).message}`);
      }
    }
  }

  async function setStatus(next: "active" | "complete") {
    if (next === "complete") {
      // Locks every round at once — closing one round already warns when holes are
      // outstanding, so ending the whole event (a bigger, similarly reversible-only-
      // by-reopening action) shouldn't skip both that warning and a plain confirm.
      const outstanding = roundResults.reduce(
        (sum, r) => sum + Math.max(0, r.holesTotal - r.holesScored),
        0,
      );
      const detail =
        outstanding > 0
          ? ` ${outstanding} hole${outstanding === 1 ? "" : "s"} across your rounds ${outstanding === 1 ? "has" : "have"} no score yet.`
          : "";
      const ok = confirm(
        `End ${TERMS.event}? This locks every round — nobody, including you, can enter more scores unless you reopen it.${detail}`,
      );
      if (!ok) return;
    }
    setStatus_(next === "complete" ? "Ending…" : "Reopening…");
    try {
      await adminOp(code, adminToken, { type: "adminSetStatus", status: next });
      setStatus_(next === "complete" ? "Ended." : "Reopened.");
    } catch (err) {
      setError_(`Failed: ${(err as Error).message}`);
    }
  }

  async function setSpectators(enabled: boolean) {
    try {
      await adminOp(code, adminToken, { type: "adminSetSpectators", enabled });
    } catch (err) {
      setError_(`Failed: ${(err as Error).message}`);
    }
  }

  async function setRoundLocked(roundId: string, locked: boolean) {
    // Closing a round makes it final over the holes that were played, which is the
    // point — but say so when holes are still outstanding rather than letting the
    // scores quietly stop where they are.
    if (locked) {
      const result = roundResults.find((r) => r.roundId === roundId);
      if (result && result.holesScored < result.holesTotal) {
        const left = result.holesTotal - result.holesScored;
        const ok = confirm(
          `${left} ${left === 1 ? "hole has" : "holes have"} no score yet. Closing the round ` +
            `now makes it final and scores it over the ${result.holesScored} ` +
            `${result.holesScored === 1 ? "hole" : "holes"} everyone finished. You can reopen it later.`,
        );
        if (!ok) return;
      }
    }
    try {
      await adminOp(code, adminToken, { type: "adminSetRoundLocked", roundId, locked });
    } catch (err) {
      setError_(`Failed: ${(err as Error).message}`);
    }
  }

  async function declareWinner(playerId: string | null) {
    try {
      await adminOp(code, adminToken, { type: "adminDeclareWinner", playerId });
      setStatus_(playerId ? "Winner declared." : "Winner cleared.");
    } catch (err) {
      setError_(`Failed: ${(err as Error).message}`);
    }
  }

  async function deleteThis() {
    setStatus_("Deleting…");
    try {
      await deleteOwnTournament(code, adminToken);
      forgetTournament(code);
      navigate("/");
    } catch (err) {
      setError_(`Failed to delete: ${(err as Error).message}`);
    }
  }

  /**
   * Says which name on the roster is the organiser themselves — most of them are
   * playing in the game they're running, and until now the app had nowhere to put that.
   *
   * Saves first, because a seat points at a player id and a row typed a moment ago
   * doesn't exist on the server yet.
   */
  async function pickSeat(playerId: string | null) {
    const row = playerId ? players.find((p) => p.id === playerId) : null;
    if (playerId && !row?.name.trim()) {
      setError_("Give this player a name first — it's what everyone else will tap.");
      return;
    }
    // Everything this tap is about to do that the tap doesn't say, gathered into one
    // question. Both of these can be true at once — moving off a scored name onto one
    // a phone holds — and two native dialogs back to back read as nagging rather than
    // as a warning, which is how a warning stops being read.
    const warnings: string[] = [];

    // Moving a seat doesn't move scores: they're filed under the player id, so the card
    // you've been filling in stays with the name you filled it in under. That is right,
    // but it is not what "that's me" sounds like, so it gets said before it happens.
    const leaving = seatId ? players.find((p) => p.id === seatId) : null;
    if (leaving && leaving.id !== playerId && scoresFor(leaving.id) > 0) {
      warnings.push(
        `${leaving.name || "That player"} already has scores in this ${TERMS.event}, and they stay ` +
          `on that name — moving your seat changes who you are from here on, not who ` +
          `those scores belong to.`,
      );
    }

    // A name a phone is holding can still be taken, but not quietly: that device stops
    // being this player, and it might be somebody standing on the tee rather than the
    // organiser's own spare handset. Which of the two it is, only they know — so this
    // says what happens and leaves the judgement to them.
    const takeover = playerId != null && offerFor(playerId) === "takeover";
    if (takeover) {
      const who = row!.name.trim() || "this player";
      warnings.push(
        `${who} is already joined on a phone. Taking the name means that phone stops ` +
          `being ${who} and has to join again.`,
      );
    }

    if (warnings.length > 0 && !confirm(`${warnings.join("\n\n")}\n\nCarry on?`)) return;
    if (!(await saveAll())) return;
    try {
      await adminOp(code, adminToken, { type: "adminSetOrganizerPlayer", playerId, takeover });
      // Written straight away so the app bar updates on this tap rather than on the
      // broadcast; the state frame that follows confirms it through syncOrganizerSeat.
      setAuth(code, { token: adminToken, role: "admin", playerId });
      setStatus_(
        playerId
          ? `You're on the roster as ${row!.name.trim()}.`
          : "You're not playing in this one.",
      );
    } catch (err) {
      setError_(`Couldn't save that: ${(err as Error).message}`);
    }
  }

  return html`
    <h1 class="mb-0">${name || "Untitled"}</h1>
    <p class="course-line">Join code <strong class="mono">${code}</strong></p>
    <${ShareButton} code=${code} name=${name} />

    <div class="panel">
      <label class="field">
        <span class="field-label">${`${TERMS.Event} name`}</span>
        <input value=${name} onInput=${(e: any) => setName(e.currentTarget.value)} />
      </label>
    </div>

    <div class="panel">
      <div class="panel-head">
        <h2>Players</h2>
        <span class="badge">${players.length}</span>
      </div>
      <p class="field-help mt-0">
        Handicap index is each player's own index — decimals are fine (e.g. 12.4). Strokes are worked
        out per hole from the course's stroke index when scoring.
      </p>

      ${seatId === null &&
      snapshot.tournament.players.length > 0 &&
      html`
        <!-- The complaint this whole feature came from: you build the roster, and the
             app assumes you're only running the thing. Asked here rather than at
             creation, because at creation there are no names to point at yet. -->
        <p class="notice mt-0">
          <${Icon} name="flag" size=${16} />${" "}
          ${`Playing in this one yourself? Tap “that's me” beside your name — your scores
            will land on you, and you can switch to playing from the badge up top.`}
        </p>
      `}
      ${seatId !== null &&
      html`
        <p class="field-help mt-0">
          ${"You're playing as "}
          <strong>${snapshot.tournament.players.find((p) => p.id === seatId)?.name}</strong>.${" "}
          <!-- Taking a seat and playing on it are two acts, and you're mid-setup here —
               so this offers the switch rather than making it. -->
          <button
            class="linkish"
            onClick=${() => {
              setAppMode(code, "player");
              navigate(`/t/${code}/leaderboard`);
            }}
          >
            Switch to playing
          </button>${" · "}
          <button class="linkish" onClick=${() => pickSeat(null)}>I'm not playing</button>
        </p>
      `}
      <div class="stack">
        ${players.map(
        (p, idx) => html`
          <div class="player-edit-row" key=${p.id}>
            <label class="field grow">
              <span class="field-label">Name</span>
              <input
                placeholder="e.g. Alice"
                value=${p.name}
                onInput=${(e: any) => updatePlayer(idx, { name: e.currentTarget.value })}
              />
            </label>
            <label class="field field-narrow">
              <span class="field-label">Handicap index</span>
              <input
                type="number"
                step="0.1"
                inputmode="decimal"
                value=${p.handicapIndex}
                onInput=${(e: any) => updatePlayer(idx, { handicapIndex: numOr(e.currentTarget.value, 0) })}
              />
            </label>
              <button
                class=${`secondary seat-btn ${p.id === seatId ? "is-seat" : ""}`}
                aria-pressed=${p.id === seatId}
                title=${
                  offerFor(p.id) === "takeover"
                    ? `${p.name || "This player"} has been joined on a phone — tap to take the name back`
                    : p.id === seatId
                      ? "You're playing as this player — tap to stop"
                      : "That's me"
                }
                aria-label=${
                  p.id === seatId
                    ? `Stop playing as ${p.name || "this player"}`
                    : `Play as ${p.name || "this player"}`
                }
                onClick=${() => pickSeat(p.id === seatId ? null : p.id)}
              >
                <${Icon} name=${p.id === seatId ? "check" : "user"} size=${18} />
              </button>
              <button
                class="secondary remove-btn"
                aria-label=${`Remove ${p.name || "player"}`}
                onClick=${() => removePlayer(idx)}
              >
                <${Icon} name="close" size=${18} />
              </button>
            </div>
          `,
        )}
      </div>
      <button
        class="secondary mt-3"
        onClick=${() => setPlayers((prev) => [...prev, blankPlayer()])}
      >
        <${Icon} name="plus" size=${18} /> Add player
      </button>
    </div>

    <div class="panel-head mt-5">
      <h2 class="m-0">Rounds</h2>
      <span class="badge">${rounds.length}</span>
    </div>
    ${rounds.map(
      (round, idx) => html`
        <${RoundEditor}
          key=${round.id}
          round=${round}
          courses=${courses}
          players=${players.filter((p) => p.name.trim())}
          playerNames=${Object.fromEntries(players.map((p) => [p.id, p.name]))}
          onChange=${(patch: Partial<Round>) => updateRound(idx, patch)}
          onRemove=${() => removeRound(idx)}
        />
      `,
    )}
    <button class="secondary" onClick=${() => setRounds((prev) => [...prev, blankRound()])}>
      <${Icon} name="plus" size=${18} /> Add round
    </button>

    <div class="panel">
      <label class="field">
        <span class="field-label">Final standings tiebreak</span>
        <select
          value=${tiebreaker}
          onChange=${(e: any) => setTiebreaker(e.currentTarget.value as FinalTiebreakRule)}
        >
          <option value="split">Split (default — ties stay tied)</option>
          <option value="mostRoundWins">Most round wins</option>
          <option value="bestSingleRound">Best single round</option>
          <option value="lowestNetTotal">Lowest net total</option>
        </select>
      </label>
    </div>

    <div class="stack mt-4">
      <button onClick=${save}><${Icon} name="check" size=${18} /> Save</button>
      ${status &&
      html`<p class=${`notice ${status.ok ? "notice-ok" : "notice-error"}`}>${status.text}</p>`}
    </div>

    <div class="panel mt-5">
      <h2>${TERMS.Organizer}</h2>
      <p class="field-help mt-0">
        ${`Only you can do these. Closing a round stops everyone else entering scores for it — you can still correct them yourself.`}
      </p>

      ${rounds.length > 0 &&
      html`
        <div class="stack">
          ${rounds.map((r) => {
            const live = snapshot.tournament.rounds.find((x) => x.id === r.id);
            const locked = Boolean(live?.locked);
            return html`
              <div class="row row-between" key=${`lock-${r.id}`}>
                <span class="grow">
                  ${r.name || "Untitled round"} ${locked ? html`<span class="badge">closed</span>` : ""}
                </span>
                <button
                  class="secondary"
                  disabled=${!live}
                  onClick=${() => setRoundLocked(r.id, !locked)}
                >
                  ${locked ? "Reopen" : "Close"}
                </button>
              </div>
            `;
          })}
        </div>
      `}

      <hr class="divider" />

      <div class="field">
      <span class="field-label">Winner</span>
      <p class="field-help mt-0">
        Optional. Overrides nothing in the maths — it's shown on the standings as your call.
      </p>
      <select
        value=${snapshot.tournament.declaredWinnerId ?? ""}
        onChange=${(e: any) => declareWinner(e.currentTarget.value || null)}
      >
        <option value="">No declared winner</option>
        ${snapshot.tournament.players.map(
          (p) => html`<option key=${p.id} value=${p.id}>${p.name}</option>`,
        )}
        </select>
      </div>

      <hr class="divider" />

      <label class="row-between">
        <span>
          Let people watch
          <small class="list-sub">
            ${`Anyone with the code can follow the standings without taking a name. They can't enter scores.`}
          </small>
        </span>
        <input
          type="checkbox"
          checked=${snapshot.tournament.spectatorsEnabled !== false}
          onChange=${(e: any) => setSpectators(e.currentTarget.checked)}
        />
      </label>

      <hr class="divider" />

      <div class="stack">
      ${snapshot.tournament.status === "complete"
        ? html`<button class="secondary" onClick=${() => setStatus("active")}>
            ${`Reopen ${TERMS.event}`}
          </button>`
        : html`<button class="secondary" onClick=${() => setStatus("complete")}>
            ${`End ${TERMS.event} (locks every round)`}
          </button>`}
      <${ShareOrCopyButton}
        label=${`Save your ${TERMS.organizer} link`}
        url=${organiserUrl(code, adminToken)}
        title=${`${TERMS.Organizer} access — ${name || code}`}
        text=${`${TERMS.Organizer} link for "${name || code}". Keep this: it's the only way back into setup if this phone loses its data. Anyone with this link has full ${TERMS.organizer} access, and it doesn't expire.`}
      />
      <p class="field-help mt-0">
        ${`Send this to yourself and keep it. ${TERMS.Organizer} access lives only on this phone, so`}
        clearing your browser data — or not opening the app for a week — would otherwise
        lock you out of setup for good.
          <strong>This link never expires and can't be revoked</strong> — anyone who gets it
          can manage this ${TERMS.event} forever, so only share it with yourself.
        </p>
      </div>
    </div>

    <div class="panel">
      <span class="eyebrow">Danger zone</span>
      <p class="field-help">
        ${`Deleting removes this ${TERMS.event} for everyone, permanently. There's no undo and no backup.`}
      </p>
      ${confirmDelete
        ? html`
            <div class="card card-danger mb-0">
              <p>Permanently delete <strong>${name || code}</strong> and every score in it?</p>
              <div class="row">
                <button class="danger grow" onClick=${deleteThis}>Yes, delete it</button>
                <button class="secondary" onClick=${() => setConfirmDelete(false)}>Cancel</button>
              </div>
            </div>
          `
        : html`<button class="secondary danger-quiet" onClick=${() => setConfirmDelete(true)}>
            ${`Delete this ${TERMS.event}`}
          </button>`}
    </div>
  `;
}

function RoundEditor({
  round,
  courses,
  players,
  playerNames,
  onChange,
  onRemove,
}: {
  round: Round;
  courses: Course[];
  players: Player[];
  playerNames: Record<string, string>;
  onChange: (patch: Partial<Round>) => void;
  onRemove: () => void;
}) {
  const playerIds = players.map((p) => p.id);
  // describeRules reads player count, so give it a plausible field even before any
  // players have been entered — otherwise the preview reads "0 players".
  const previewPlayers: Player[] =
    players.length > 0
      ? players
      : Array.from({ length: 4 }, (_, i) => ({
          id: `preview-${i}`,
          name: `Player ${i + 1}`,
          handicapIndex: 0,
          token: null,
          claimedAt: null,
        }));
  const [segmentCount, setSegmentCount] = useState(Math.max(round.segments.length, 1));
  const course = courses.find((c) => c.id === round.courseId);
  const selectedTee = course?.teeSets.find((t) => t.name === round.teeSet);
  const format = formats[round.formatId];
  const coverage = useMemo(() => computeCoverageMatrix(playerIds, round.segments), [playerIds, round.segments]);

  // Off the card, not off round.holeSet: on a nine-hole course "all of them" is nine.
  //
  // Falls back to the course's first tee rather than only reading the selected one,
  // because picking a course clears the tee — so for the moment between the two clicks
  // `selectedTee` is undefined, and reading only that would offer a back nine on a
  // nine-hole course until the second click took it away again. Every tee on a course
  // shares one set of holes, so the first speaks for all of them.
  const cardHoles = (selectedTee ?? course?.teeSets[0])?.holes.length ?? FULL_ROUND;
  const nineHoleCourse = cardHoles <= HALF_ROUND;
  const holeCount = holesPlayedCount((selectedTee ?? course?.teeSets[0])?.holes, round.holeSet);
  // Segment bounds are positions within the round's hole set, so on a back nine
  // position 1 is hole 10 — the editor has to say which it means. A nine-hole course
  // has no back nine, so its positions are always its own hole numbers.
  const holeOffset = !nineHoleCourse && round.holeSet === "back9" ? HALF_ROUND : 0;
  // Two sources: the engine's own invariants about sides, plus whatever the format
  // itself knows that they can't see (Wolf's field size, an unpaired match play side).
  const problems = useMemo(
    () => [
      ...validateSegments(playerIds, round.segments, holeCount, {
        nameOf: (id) => playerNames[id] ?? id,
        requiresSides: format?.scope === "team",
      }),
      ...(format?.validate?.(round, players) ?? []),
    ],
    [playerIds, round, holeCount, playerNames, format, players],
  );

  function generateRotation() {
    if (playerIds.length < 2) return;
    if (
      round.segments.length > 0 &&
      !confirm("Replace the current pairings with a freshly generated rotation?")
    ) {
      return;
    }
    // Match play's sides are opponents, not partnerships, so singles wants one player a
    // side — the team rotation would silently build four-ball instead.
    const singles =
      round.formatId === "matchPlay" &&
      ((round.formatConfig as { sides?: string })?.sides ?? "singles") === "singles";
    const teamRounds = singles
      ? generateSinglesRotation(playerIds, segmentCount)
      : generateTeamRotation(playerIds, segmentCount);
    const ranges = evenHoleRanges(holeCount, segmentCount);
    const segments = teamRounds.map((teams, i) => ({
      startHole: ranges[i]!.startHole,
      endHole: ranges[i]!.endHole,
      teams,
    }));
    onChange({ segments });
  }

  /** Segments are replaced wholesale — Round only takes a shallow patch. */
  function updateSegment(idx: number, patch: Partial<Segment>) {
    onChange({ segments: round.segments.map((s, i) => (i === idx ? { ...s, ...patch } : s)) });
  }

  /** Moves one player onto side `teamIdx`, or off every side when it's null. A player
   * on two sides at once breaks single-ball scoring, so this removes before it adds. */
  function assignPlayer(segIdx: number, playerId: string, teamIdx: number | null) {
    const seg = round.segments[segIdx]!;
    const teams = seg.teams.map((t) => t.filter((id) => id !== playerId));
    while (teamIdx != null && teams.length <= teamIdx) teams.push([]);
    if (teamIdx != null) teams[teamIdx]!.push(playerId);
    updateSegment(segIdx, { teams });
  }

  // Null when every hole is already a segment of its own, which is the one case where
  // there is nothing left to add. See segmentsWithOneMore for why appending naively
  // used to leave the round unsaveable.
  const withOneMoreSegment = segmentsWithOneMore(round.segments, holeCount);

  function addSegment() {
    if (withOneMoreSegment) onChange({ segments: withOneMoreSegment });
  }

  return html`
    <div class="panel">
      <div class="row row-bottom">
        <label class="field grow">
          <span class="field-label">Round name</span>
          <input
            placeholder="e.g. Saturday Morning"
            value=${round.name}
            onInput=${(e: any) => onChange({ name: e.currentTarget.value })}
          />
        </label>
        <button class="secondary remove-btn" aria-label="Remove round" onClick=${onRemove}>
          <${Icon} name="close" size=${18} />
        </button>
      </div>

      <div class="row mt-3">
        <label class="field grow">
          <span class="field-label">Course</span>
          <select
            value=${round.courseId}
            onChange=${(e: any) => {
              const courseId = e.currentTarget.value;
              const picked = courses.find((c) => c.id === courseId);
              onChange({
                courseId,
                teeSet: "",
                ...narrowToCard(picked?.teeSets[0]?.holes.length, round),
              });
            }}
          >
            <option value="">Select course…</option>
            ${courses.map((c) => html`<option key=${c.id} value=${c.id}>${c.name}</option>`)}
          </select>
        </label>
        <label class="field grow">
          <span class="field-label">Tee set</span>
          <select
            value=${round.teeSet}
            onChange=${(e: any) => {
              const teeName = e.currentTarget.value;
              const tee = course?.teeSets.find((t) => t.name === teeName);
              onChange({ teeSet: teeName, ...narrowToCard(tee?.holes.length, round) });
            }}
          >
            <option value="">Select tee…</option>
            ${(course?.teeSets ?? []).map(
              (t) => html`<option key=${t.name} value=${t.name}>${teeLabel(t)}</option>`,
            )}
          </select>
        </label>
      </div>
      ${selectedTee &&
      html`<p class="field-help">
        ${`Playing the ${selectedTee.name} tees${selectedTee.slope ? ` — slope ${selectedTee.slope}, which sets how many strokes each handicap gets` : " (no slope on file, so handicap indexes are used as-is)"}.`}
      </p>`}
      ${courses.length === 0 &&
      html`<p class="field-help">
        ${`No courses in the library yet. Courses are shared by every ${TERMS.event} and are managed by the admin — ask whoever runs this app to add the one you're playing.`}
      </p>`}

      <div class="row mt-2">
        <label class="field grow">
          <span class="field-label">Holes played</span>
          <select
            ${/* Not `round.holeSet` directly: a round built when its course had eighteen
                 holes still carries "back9" after an admin re-enters that course as a
                 nine, and a disabled select whose value matches none of its options
                 renders blank with no way to fix it. What it plays is the whole card —
                 holesInPlay says so — and that is what this shows. saveAll writes the
                 correction back. */
            ""}
            value=${nineHoleCourse ? "all18" : round.holeSet}
            disabled=${nineHoleCourse}
            onChange=${(e: any) => {
              const holeSet = e.currentTarget.value as HoleSet;
              // Segment bounds are positions in the hole set, so switching 18 -> 9
              // would otherwise leave ranges running past the end of the round,
              // which holesInSegment silently truncates to nothing.
              const total = holesPlayedCount((selectedTee ?? course?.teeSets[0])?.holes, holeSet);
              const ranges = evenHoleRanges(total, round.segments.length || 1);
              onChange({
                holeSet,
                ...(round.segments.length > 0
                  ? {
                      segments: round.segments.map((s, i) => ({
                        ...s,
                        startHole: ranges[i]?.startHole ?? s.startHole,
                        endHole: ranges[i]?.endHole ?? s.endHole,
                      })),
                    }
                  : {}),
              });
            }}
          >
            ${/* A nine-hole course has one option, and it isn't a choice: there is no
                 front or back nine to pick between. The select stays (rather than
                 vanishing) so the row doesn't reflow when a course is chosen, and so it
                 still says what the round plays. */
            nineHoleCourse
              ? html`<option value="all18">All 9</option>`
              : html`
                  <option value="all18">All 18</option>
                  <option value="front9">Front 9</option>
                  <option value="back9">Back 9</option>
                `}
          </select>
        </label>
        <label class="field grow">
          <span class="field-label">Scoring format</span>
          <select
            value=${round.formatId}
            onChange=${(e: any) => {
              const nextId = e.currentTarget.value as string;
              const next = formats[nextId];
              onChange({
                formatId: nextId,
                // Start from the new format's conventional allowance (spec §6) and
                // drop config belonging to the old format, which wouldn't apply.
                ...(next?.defaults?.handicapAllowance != null
                  ? { handicapAllowance: next.defaults.handicapAllowance }
                  : {}),
                formatConfig: {},
                // An individual format has no sides, but scorableHoles still reads
                // leftover segments and would decide holes on teams nobody is playing.
                ...(next?.scope === "individual" ? { segments: [] } : {}),
              });
            }}
          >
            ${Object.values(formats).map((f) => html`<option key=${f.id} value=${f.id}>${f.name}</option>`)}
          </select>
        </label>
      </div>

      ${format?.uses.pointTable &&
      html`
      <div class="field mt-3">
        <span class="field-label">Points by finishing place</span>
        <p class="field-help">
          What each placing is worth in this round. Players who tie split the points for the places
          they cover.
        </p>
        <div class="place-list">
          ${round.pointTable.map(
            (points, i) => html`
              <div class="place-row" key=${i}>
                <span class="place-label">${ordinal(i + 1)}</span>
                <input
                  type="number"
                  inputmode="numeric"
                  value=${points}
                  onInput=${(e: any) => {
                    const next = [...round.pointTable];
                    next[i] = numOr(e.currentTarget.value, 0);
                    onChange({ pointTable: next });
                  }}
                />
                <button
                  class="secondary"
                  aria-label=${`Remove ${ordinal(i + 1)} place`}
                  onClick=${() => onChange({ pointTable: round.pointTable.filter((_, j) => j !== i) })}
                >
                  <${Icon} name="close" size=${18} />
                </button>
              </div>
            `,
          )}
          ${round.pointTable.length === 0 &&
          html`<p class="field-help">No places yet — add one below.</p>`}
        </div>
        <button
          class="secondary"
          onClick=${() => onChange({ pointTable: [...round.pointTable, 0] })}
        >
          <${Icon} name="plus" size=${18} /> Add place
        </button>
      </div>
      `}

      ${format?.uses.teamPoints &&
      html`
        <div class="row mt-3">
          <label class="field grow">
            <span class="field-label">Team win points</span>
            <input
              type="number"
              value=${round.teamPoints?.win ?? 0}
              onInput=${(e: any) =>
                onChange({ teamPoints: { win: numOr(e.currentTarget.value, 0), loss: round.teamPoints?.loss ?? 0 } })}
            />
          </label>
          <label class="field grow">
            <span class="field-label">Team loss points</span>
            <input
              type="number"
              value=${round.teamPoints?.loss ?? 0}
              onInput=${(e: any) =>
                onChange({ teamPoints: { win: round.teamPoints?.win ?? 0, loss: numOr(e.currentTarget.value, 0) } })}
            />
          </label>
        </div>
      `}

      <div class="row mt-3">
        ${format?.uses.handicapAllowance &&
        html`
          <label class="field grow">
            <span class="field-label">Handicap allowance %</span>
            <input
              type="number"
              inputmode="numeric"
              value=${round.handicapAllowance}
              onInput=${(e: any) => onChange({ handicapAllowance: numOr(e.currentTarget.value, 100) })}
            />
          </label>
        `}
        <label class="field grow">
          <span class="field-label">Points multiplier</span>
          <input
            type="number"
            step="0.1"
            inputmode="decimal"
            value=${round.multiplier}
            onInput=${(e: any) => onChange({ multiplier: numOr(e.currentTarget.value, 1) })}
          />
        </label>
      </div>
      <p class="field-help">
        ${format?.uses.handicapAllowance
          ? `Allowance is the share of each player's course handicap that counts this round.${holeCount <= HALF_ROUND ? " It's halved automatically on a 9-hole round." : ""} `
          : format?.id === "scramble"
            ? "This format sets its team handicap from the lower/higher percentages below rather than a single allowance. "
            : "This format doesn't use handicaps. "}
        Multiplier scales this round's points if you want it to count for more than the others.
      </p>

      ${format?.scope === "team" &&
      html`
        <div class="mt-3">
          <div class="row row-bottom">
            <label class="field grow">
              <span class="field-label">Segments</span>
              <input
                type="number"
                min="1"
                value=${segmentCount}
                onInput=${(e: any) => setSegmentCount(numOr(e.currentTarget.value, 1))}
              />
            </label>
            <button class="secondary" onClick=${generateRotation}>Auto-generate</button>
          </div>
          <p class="field-help">
            One segment per set of holes the pairings hold for — 3, say, for a scramble with
            partner swaps at 6 and 12.
          </p>
          ${round.segments.map((seg, si) => {
            // Players are listed under the side they're on, not in roster order with the
            // grouping hidden inside each dropdown. With uneven sides — a threesome, or
            // someone sitting out — reading who is with whom off a column of selects is
            // the part that got messy.
            const benched = players.filter((p) => !seg.teams.some((t) => t.includes(p.id)));

            const sideSelect = (playerId: string, currentIdx: number | null) => html`
              <select
                aria-label="Side"
                value=${currentIdx === null ? "" : String(currentIdx)}
                onChange=${(e: any) => {
                  const v = e.currentTarget.value;
                  assignPlayer(si, playerId, v === "" ? null : Number(v));
                }}
              >
                ${seg.teams.map(
                  (_, ti) => html`<option key=${ti} value=${String(ti)}>${`Team ${ti + 1}`}</option>`,
                )}
                <option value="">Sitting out</option>
              </select>
            `;

            return html`
              <div class="segment-editor" key=${si}>
                <div class="segment-head">
                  <strong>${`Segment ${si + 1}`}</strong>
                  <span class="muted">
                    ${holeOffset > 0
                      ? `holes ${seg.startHole + holeOffset}–${seg.endHole + holeOffset}`
                      : `holes ${seg.startHole}–${seg.endHole}`}
                  </span>
                  <button
                    class="secondary remove-btn"
                    aria-label=${`Remove segment ${si + 1}`}
                    onClick=${() =>
                      onChange({ segments: round.segments.filter((_, i) => i !== si) })}
                  >
                    <${Icon} name="close" size=${18} />
                  </button>
                </div>

                <div class="row">
                  <label class="field grow">
                    <span class="field-label">From hole</span>
                    <input
                      type="number"
                      min="1"
                      max=${holeCount}
                      value=${seg.startHole}
                      onInput=${(e: any) =>
                        updateSegment(si, { startHole: numOr(e.currentTarget.value, seg.startHole) })}
                    />
                  </label>
                  <label class="field grow">
                    <span class="field-label">To hole</span>
                    <input
                      type="number"
                      min="1"
                      max=${holeCount}
                      value=${seg.endHole}
                      onInput=${(e: any) =>
                        updateSegment(si, { endHole: numOr(e.currentTarget.value, seg.endHole) })}
                    />
                  </label>
                </div>
                ${holeOffset > 0 &&
                html`<p class="field-help">
                  ${`Numbered from the start of this round, so 1 is hole ${1 + holeOffset}.`}
                </p>`}

                ${seg.teams.map(
                  (team, ti) => html`
                    <div class="side-group" key=${ti}>
                      <div class="side-head">
                        <span class="side-name">${`Team ${ti + 1}`}</span>
                        <span class="side-count">
                          ${team.length === 0
                            ? "nobody yet"
                            : `${team.length} player${team.length === 1 ? "" : "s"}`}
                        </span>
                        ${seg.teams.length > 2 &&
                        html`<button
                          class="linklike"
                          aria-label=${`Remove team ${ti + 1}`}
                          onClick=${() =>
                            updateSegment(si, { teams: seg.teams.filter((_, i) => i !== ti) })}
                        >
                          remove
                        </button>`}
                      </div>
                      ${team.length === 0
                        ? html`<p class="side-empty">Move a player here with their dropdown.</p>`
                        : team.map(
                            (id) => html`
                              <div class="segment-player-row" key=${id}>
                                <span>${playerNames[id] ?? id}</span>
                                ${sideSelect(id, ti)}
                              </div>
                            `,
                          )}
                    </div>
                  `,
                )}

                ${benched.length > 0 &&
                html`
                  <div class="side-group side-group-out">
                    <div class="side-head">
                      <span class="side-name">Sitting out</span>
                      <span class="side-count">${`${benched.length} not playing these holes`}</span>
                    </div>
                    ${benched.map(
                      (p) => html`
                        <div class="segment-player-row" key=${p.id}>
                          <span>${p.name}</span>
                          ${sideSelect(p.id, null)}
                        </div>
                      `,
                    )}
                  </div>
                `}

                <button
                  class="linklike"
                  onClick=${() => updateSegment(si, { teams: [...seg.teams, []] })}
                >
                  + Add a team
                </button>
              </div>
            `;
          })}

          <div class="row mt-2">
            <button class="secondary" disabled=${!withOneMoreSegment} onClick=${addSegment}>
              <${Icon} name="plus" size=${18} /> Add segment
            </button>
            ${round.segments.length > 0 &&
            html`<button class="secondary" onClick=${() => onChange({ segments: [] })}>
              Clear pairings
            </button>`}
          </div>

          ${problems.map(
            (p, i) => html`<p
              key=${i}
              class=${`field-help ${p.level === "error" ? "text-danger" : ""}`}
            >
              ${`${p.level === "error" ? "!" : "·"} ${p.message}`}
            </p>`,
          )}

          ${round.segments.length > 0 &&
          html`
            <p class="muted">
              Pairing coverage:
              ${[...coverage.entries()]
                .map(([key, count]) => {
                  const [a, b] = key.split("|");
                  return `${playerNames[a!] ?? a} + ${playerNames[b!] ?? b}: ${count}×`;
                })
                .join(" · ")}
            </p>
          `}
        </div>
      `}

      ${format &&
      html`<${FormatConfigForm}
        schema=${format.configSchema}
        value=${round.formatConfig ?? {}}
        onChange=${(formatConfig: Record<string, unknown>) => {
          // Switching between singles and four-ball changes what the conventional
          // allowance is, so move it with the choice — the same courtesy picking a
          // format gets from Format.defaults. An organiser who wants something else
          // still edits the field afterwards.
          const sides = formatConfig["sides"];
          const wasSides = (round.formatConfig ?? {})["sides"] ?? "singles";
          if (round.formatId === "matchPlay" && sides !== wasSides && typeof sides === "string") {
            onChange({
              formatConfig,
              handicapAllowance: conventionalAllowance(sides === "fourBall" ? "fourBall" : "singles"),
            });
            return;
          }
          onChange({ formatConfig });
        }}
      />`}

      ${format &&
      html`
        <details class="rules-preview" open>
          <summary class="field-label">How this round scores</summary>
          <ul>
            ${describeRoundRules(round, previewPlayers, selectedTee).map(
              (line, i) => html`<li key=${i}>${line}</li>`,
            )}
          </ul>
          <p class="field-help mb-0">
            Generated from the settings above, so it always matches how the round actually scores.
            Players see this same text on the scorecard.
          </p>
        </details>
      `}
    </div>
  `;
}
